import { useQuery, useMutation } from '@apollo/client'
import { ToastContext } from 'contexts'
import { useContext } from 'react'

import createSubnetApiServiceGql from 'gql/subnet-api/createSubnetApiService.gql'
import updateSubnetApiServiceGql from 'gql/subnet-api/updateSubnetApiService.gql'
import getSubnetApiServicesGql from 'gql/subnet-api/getSubnetApiServices.gql'
import getSubnetApiServiceByIdGql from 'gql/subnet-api/getSubnetApiServiceById.gql'
import deleteSubnetApiServiceGql from 'gql/subnet-api/deleteSubnetApiService.gql'
import getSubnetsGql from 'gql/subnet-api/getSubnets.gql'
import updateSubnetGql from 'gql/subnet-api/updateSubnet.gql'
import subnetByIdGql from 'gql/subnet-api/subnetById.gql'
import apiServicesGql from 'gql/subnet-api/apiServices.gql'
import {
	// SubnetApiService,
	SubnetApiServiceInput,
	SubnetInput,
	UseCreateSubnetApiServiceReturn,
	UseDeleteSubnetApiServiceReturn,
	UseGetSubnetApiServiceByIdReturn,
	UseGetSubnetApiServicesReturn,
	UseGetSubnets,
	UseUpdateSubnetApiServiceReturn,
} from 'types/subnetApiService'

/**
 * Asynchronously creates a Subnet Api Service.
 *
 * @param {SubnetApiServiceInput} input - The input data for creating the Subnet Api Service.
 * @return {Promise<UseCreateSubnetApiServiceReturn>} The created Subnet Api Service.
 */
export const useCreateSubnetApiService = () => {
	const { setToast } = useContext(ToastContext)
	const [mutation, { loading }] = useMutation(createSubnetApiServiceGql, {
		update(
			cache,
			{
				data: {
					createSubnetApiService: { subnet_api_service },
				},
			},
		) {
			// const queryResponse: any = cache.readQuery({ query: getSubnetApiServicesGql })
			// cache.writeQuery({
			// 	query: getSubnetApiServicesGql,
			// 	data: { getSubnetApiServices: [subnet_api_service] },
			// })
		},
	})

	const createSubnetApiService = async (
		input: SubnetApiServiceInput,
	): Promise<UseCreateSubnetApiServiceReturn> => {
		try {
			const {
				data: { createSubnetApiService },
			} = await mutation({
				variables: {
					input: input,
				},
			})

			return createSubnetApiService
		} catch (error) {
			setToast({
				message: (error as Error)?.message ?? 'Error creating Subnet Api Service',
				type: 'negative',
				open: true,
			})
			throw error // Rethrow the error to propagate it to the caller
		}
	}

	return { createSubnetApiService, loading }
}

/**
 * Asynchronously updates a Subnet Api Service.
 *
 * @param {string} subnet_api_service_id - The ID of the subnet api service to update.
 * @param {SubnetApiServiceInput} input - The input data for updating the Subnet Api Service.
 * @return {Promise<UseUpdateSubnetApiServiceReturn>} The updated Subnet Api Service.
 */
export const useUpdateSubnetApiService = () => {
	const { setToast } = useContext(ToastContext)
	const [mutation, { loading }] = useMutation(updateSubnetApiServiceGql)

	const updateSubnetApiService = async (
		subnet_api_service_id: string,
		input: SubnetApiServiceInput,
	): Promise<UseUpdateSubnetApiServiceReturn> => {
		try {
			const {
				data: { updateSubnetApiService },
			} = await mutation({
				variables: {
					subnet_api_service_id,
					input: input,
				},
			})

			return updateSubnetApiService
		} catch (error) {
			setToast({
				message: (error as Error)?.message ?? 'Error updating Subnet Api Service',
				type: 'negative',
				open: true,
			})
			throw error // Rethrow the error to propagate it to the caller
		}
	}

	return { updateSubnetApiService, loading }
}

/**
 * Fetches the subnet API services using the `getSubnetApiServicesGql` query.
 *
 * getSubnetApiServices @param {string} subnet_id - The ID of the subnet to fetch the API services for.
 * @return {UseGetSubnetApiServicesReturn} An object containing the fetched data, error, loading status, and refetch function.
 */
export const useGetSubnetApiServices = (id: string | undefined): UseGetSubnetApiServicesReturn => {
	const { data, error, loading, refetch } = useQuery(getSubnetApiServicesGql, {
		variables: { subnet_id: id },
		fetchPolicy: 'cache-first',
	})

	return {
		data: data?.getSubnetApiServices || [],
		error,
		loading,
		refetch,
	}
}

/**
 * Fetches the subnet API service by ID and handles errors.
 *
 * @param {string} subnet_api_service_id - The ID of the subnet API service to fetch.
 * @returns {UseGetSubnetApiServiceByIdReturn} The fetched data, error, loading status, and refetch function.
 */
export const useGetSubnetApiServiceById = (
	subnet_api_service_id: string | undefined,
): UseGetSubnetApiServiceByIdReturn => {
	const { data, error, loading, refetch } = useQuery(getSubnetApiServiceByIdGql, {
		variables: { subnet_api_service_id },
	})

	try {
		if (error) {
			// Handle error here
			console.error('Error fetching Subnet Api Service by id:', error)
		}
	} catch (error) {
		// Handle any unexpected errors
		console.error('An unexpected error occurred:', error)
	}

	return {
		data: data?.getSubnetApiServiceById || {},
		error,
		loading,
		refetch,
	}
}

/**
 * A hook that provides a function to delete a Subnet Api Service.
 * @param {string} subnet_api_service_id - The ID of the Subnet Api Service to delete.
 * @return {{ deleteSubnetApiService: (subnet_api_service_id: string) => Promise<UseDeleteSubnetApiServiceReturn>; loading: boolean }} An object containing the deleteSubnetApiService function and the loading state.
 */
export const useDeleteSubnetApiService = () => {
	const { setToast } = useContext(ToastContext)
	const [mutation, { loading }] = useMutation(deleteSubnetApiServiceGql)

	const deleteSubnetApiService = async (
		subnet_api_service_id: string,
	): Promise<UseDeleteSubnetApiServiceReturn> => {
		try {
			const {
				data: { deleteSubnetApiService },
			} = await mutation({
				variables: {
					subnet_api_service_id,
				},
			})

			return deleteSubnetApiService
		} catch (error) {
			setToast({
				message: (error as Error)?.message ?? 'Error deleting Subnet Api Service',
				type: 'negative',
				open: true,
			})
			throw error // Rethrow the error to propagate it to the caller
		}
	}

	return { deleteSubnetApiService, loading }
}

/**
 * Fetches the subnets using the `getSubnetsGql` query and handles errors.
 *
 * @return {UseGetSubnets} An object containing the fetched data, error, loading status, and refetch function.
 */
export const useGetSubnets = (): UseGetSubnets => {
	const { data, error, loading, refetch } = useQuery(getSubnetsGql, {
		fetchPolicy: 'cache-first',
	})

	try {
		if (error) {
			// Handle error here
			console.error('Error fetching Subnets:', error)
		}
	} catch (error) {
		// Handle any unexpected errors
		console.error('An unexpected error occurred:', error)
	}

	return {
		data: data?.getSubnets || [],
		error,
		loading,
		refetch,
	}
}

/**
 * Asynchronously updates a subnet.
 *
 * @param {string} subnet_id - The ID of the subnet to update.
 * @param {SubnetInput} input - The input data for updating the subnet.
 * @return {Promise<any>} The updated subnet data.
 */
export const useUpdateSubnet = () => {
	const { setToast } = useContext(ToastContext)
	const [mutation, { loading }] = useMutation(updateSubnetGql, {
		/**
		 * Updates the cache with the new subnet data.
		 *
		 * @param {Cache} cache - The cache object to update.
		 * @param {Object} data - The data object containing the updated subnet.
		 * @return {void} No return value.
		 */
		update(
			cache,
			{
				data: {
					updateSubnet: { subnet },
				},
			},
		) {
			cache.modify({
				fields: {
					getSubnets(existingSubnets = [], { readField }) {
						return existingSubnets.map((subnetRef: any) =>
							subnet.id === readField('id', subnetRef) ? subnet : subnetRef,
						)
					},
				},
			})
		},
	})
	const updateSubnet = async (subnet_id: string, input: SubnetInput) => {
		try {
			const {
				data: { updateSubnet },
			} = await mutation({
				variables: {
					subnet_id,
					input,
				},
			})

			return updateSubnet
		} catch (error) {
			setToast({
				message: (error as Error)?.message ?? 'Error updating Subnet',
				type: 'negative',
				open: true,
			})
			throw error // Rethrow the error to propagate it to the caller
		}
	}

	return { updateSubnet, loading }
}

/**
 * Retrieves a subnet by its ID using the `subnetByIdGql` query.
 *
 * @param {string | undefined} subnet_id - The ID of the subnet to retrieve.
 * @return {{ data: Array<Subnet>, error: ApolloError | undefined, loading: boolean, refetch: () => Promise<ApolloQueryResult<SubnetByIdQuery>> }} An object containing the fetched data, error, loading status, and refetch function.
 */
export const useSubnetById = (subnet_id: string | undefined) => {
	const { data, error, loading, refetch } = useQuery(subnetByIdGql, {
		fetchPolicy: 'cache-first',
		variables: { subnet_id },
	})

	return {
		data: data?.subnetById || [],
		error,
		loading,
		refetch,
	}
}

/**
 * Fetches the API services using the `apiServicesGql` query.
 *
 * @return {Object} An object containing the fetched data, error, loading status, and refetch function.
 * - `data` (Array): The fetched API services.
 * - `error` (ApolloError | undefined): The error, if any.
 * - `loading` (boolean): The loading status.
 * - `refetch` (() => Promise<ApolloQueryResult<ApiServicesQuery>>): The refetch function.
 */
export const useApiServices = () => {
	const { data, error, loading, refetch } = useQuery(apiServicesGql, {
		fetchPolicy: 'cache-and-network',
	})

	return {
		data: data?.apiServices || [],
		error,
		loading,
		refetch,
	}
}
